/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: ChangesModal.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Modal para mostrar los cambios (before/after) de un registro
────────────────────────────────────────────────────────────────*/

import { X, Copy, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useState } from 'react';

interface ChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: Record<string, any>;
  title?: string;
}

export function ChangesModal({
  isOpen,
  onClose,
  metadata,
  title = 'Change Details'
}: ChangesModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Extract before/after if available
  const before = metadata?.before || {};
  const after = metadata?.after || {};
  const hasBeforeAfter = Object.keys(before).length > 0 || Object.keys(after).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-8"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {hasBeforeAfter ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Before */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Before</h3>
                <pre className="bg-muted/50 p-3 rounded-md text-xs overflow-x-auto border border-border">
                  <code className="text-foreground">
                    {JSON.stringify(before, null, 2)}
                  </code>
                </pre>
              </div>

              {/* After */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">After</h3>
                <pre className="bg-muted/50 p-3 rounded-md text-xs overflow-x-auto border border-border">
                  <code className="text-foreground">
                    {JSON.stringify(after, null, 2)}
                  </code>
                </pre>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Metadata</h3>
              <pre className="bg-muted/50 p-3 rounded-md text-xs overflow-x-auto border border-border">
                <code className="text-foreground">
                  {JSON.stringify(metadata, null, 2)}
                </code>
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-border">
          <Button variant="default" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

